/**
 * Multi-Angle (Layer-Orientation) CTE Compensation
 * Takes the robot toolpath (KUKA .src, optional .dat, or G-code) and the STEP /
 * STL of the target part. Each orientation region of the print gets its own
 * rotated anisotropic CTE tensor, and the regions are blended into a single
 * displacement field used to pre-scale the geometry.
 */

import React, { useState, useRef, useMemo, useEffect, useCallback } from 'react';
import * as THREE from 'three';
import {
  integrateCTE, jetColor,
  exportBinarySTL,
} from './cte-utils';
import { ColorBar, useCadModelUpload } from './cte-shared';
import {
  parseToolpath, analyseToolpath, regionStrainTensor,
  buildLayerField, serializeLayerField, compensateMeshWithField,
  regionColor, combineKrlFiles, autoRegister,
  eulerToMatrix, matrixToEuler, invertRegistration,
} from './layer-orientation';
import CTEAIPanel from './CTEAIPanel';
import { authFetch } from '../../utils/api';
import { bindViewerBackground } from '../../utils/theme';

const MAX_PREVIEW_SEGMENTS = 150000;

const DOWNLOAD_DISCLAIMER =
  'DISCLAIMER\n\n' +
  'Rapid Fusion accepts no responsibility for the accuracy of these results. ' +
  'This tool is provided as a computational aid only.\n\n' +
  'A qualified engineer must review and verify all compensated geometry before manufacturing.\n\n' +
  'Do you wish to proceed with the download?';

const fmtVec = (v, d = 3) => `(${v.map(x => x.toFixed(d)).join(', ')})`;

function downloadBlob(blob, name) {
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = name;
  a.click();
  URL.revokeObjectURL(url);
}

// Toolpath points (toolpath frame) → model frame, as line segments per region
function buildToolpathSegments(tp, pointRegion, registration) {
  const toModel = (i) => invertRegistration(registration, tp.pos[i * 3], tp.pos[i * 3 + 1], tp.pos[i * 3 + 2]);
  let total = 0;
  for (let i = 1; i < tp.count; i++) if (pointRegion[i] >= 0 && pointRegion[i - 1] === pointRegion[i]) total++;
  const stride = Math.max(1, Math.ceil(total / MAX_PREVIEW_SEGMENTS));
  const pos = [], col = [];
  let k = 0;
  for (let i = 1; i < tp.count; i++) {
    const r = pointRegion[i];
    if (r < 0 || pointRegion[i - 1] !== r) continue;
    if ((k++ % stride) !== 0) continue;
    const a = toModel(i - 1), b = toModel(i);
    pos.push(...a, ...b);
    const cc = new THREE.Color(regionColor(r));
    col.push(cc.r, cc.g, cc.b, cc.r, cc.g, cc.b);
  }
  return { positions: new Float32Array(pos), colors: new Float32Array(col) };
}

// ─── HELP TIPS ────────────────────────────────────────────────────────────────
// A "?" next to a label; the explanation shows on hover or keyboard focus.
function HelpTip({ text }) {
  return (
    <span className="cte-help" tabIndex={0} aria-label={text}>
      <span aria-hidden="true">?</span>
      <span className="cte-help-bubble" role="tooltip">{text}</span>
    </span>
  );
}

function FieldLabel({ children, help, hint }) {
  return (
    <label className="cte-help-label">
      <span>{children}{hint && <span className="cte-help-hint"> {hint}</span>}</span>
      {help && <HelpTip text={help} />}
    </label>
  );
}

// ─── 3D VIEWER (Z-up, model frame) ────────────────────────────────────────────
function LayerFieldViewer({
  positions, displacements, vertexRegion, maxDisp, colorMode,
  toolpath, showToolpath, showOriginal, defScale = 1,
}) {
  const mountRef = useRef(null);

  useEffect(() => {
    if (!mountRef.current || (!positions && !toolpath)) return;
    const W = mountRef.current.clientWidth || 700;
    const H = mountRef.current.clientHeight || 500;

    const renderer = new THREE.WebGLRenderer({ antialias: true, preserveDrawingBuffer: true });
    renderer.setSize(W, H);
    renderer.setPixelRatio(window.devicePixelRatio || 1);
    const unbindBg = bindViewerBackground(renderer, 0x0a0a0f);
    mountRef.current.innerHTML = '';
    mountRef.current.appendChild(renderer.domElement);

    const scene = new THREE.Scene();
    const camera = new THREE.PerspectiveCamera(45, W / H, 0.01, 1e7);
    camera.up.set(0, 0, 1);
    scene.add(new THREE.AmbientLight(0xffffff, 0.6));
    const dirLight = new THREE.DirectionalLight(0xffffff, 0.8);
    dirLight.position.set(1, 1.5, 2);
    scene.add(dirLight);

    const box = new THREE.Box3();

    if (positions) {
      const nv = positions.length / 3;
      const shown = new Float32Array(positions.length);
      const colors = new Float32Array(positions.length);
      const maxMag = maxDisp > 0 ? maxDisp : 1;
      for (let i = 0; i < nv; i++) {
        let dx = 0, dy = 0, dz = 0;
        if (displacements) { dx = displacements[i * 3]; dy = displacements[i * 3 + 1]; dz = displacements[i * 3 + 2]; }
        shown[i * 3] = positions[i * 3] + dx * defScale;
        shown[i * 3 + 1] = positions[i * 3 + 1] + dy * defScale;
        shown[i * 3 + 2] = positions[i * 3 + 2] + dz * defScale;
        let rgb;
        if (colorMode === 'region' && vertexRegion) {
          const cc = new THREE.Color(regionColor(vertexRegion[i]));
          rgb = [cc.r, cc.g, cc.b];
        } else if (displacements) {
          rgb = jetColor(Math.hypot(dx, dy, dz) / maxMag);
        } else {
          rgb = [0.55, 0.55, 0.6];
        }
        colors[i * 3] = rgb[0]; colors[i * 3 + 1] = rgb[1]; colors[i * 3 + 2] = rgb[2];
      }
      const geo = new THREE.BufferGeometry();
      geo.setAttribute('position', new THREE.BufferAttribute(shown, 3));
      geo.setAttribute('color', new THREE.BufferAttribute(colors, 3));
      geo.computeVertexNormals();
      const mat = new THREE.MeshPhongMaterial({
        vertexColors: true, side: THREE.DoubleSide, shininess: 40,
        transparent: showToolpath, opacity: showToolpath ? 0.55 : 1, depthWrite: !showToolpath,
      });
      scene.add(new THREE.Mesh(geo, mat));
      geo.computeBoundingBox();
      box.union(geo.boundingBox);

      if (showOriginal && displacements) {
        const og = new THREE.BufferGeometry();
        og.setAttribute('position', new THREE.BufferAttribute(positions, 3));
        scene.add(new THREE.Mesh(og, new THREE.MeshBasicMaterial({ color: 0x9aa0b4, wireframe: true, opacity: 0.15, transparent: true })));
      }
    }

    if (showToolpath && toolpath && toolpath.positions.length) {
      const lg = new THREE.BufferGeometry();
      lg.setAttribute('position', new THREE.BufferAttribute(toolpath.positions, 3));
      lg.setAttribute('color', new THREE.BufferAttribute(toolpath.colors, 3));
      scene.add(new THREE.LineSegments(lg, new THREE.LineBasicMaterial({ vertexColors: true })));
      lg.computeBoundingBox();
      box.union(lg.boundingBox);
    }

    const centre = box.getCenter(new THREE.Vector3());
    const span = Math.max(1e-3, box.getSize(new THREE.Vector3()).length());
    const axes = new THREE.AxesHelper(span * 0.25);
    axes.position.copy(box.min);
    scene.add(axes);

    let dragging = false, lastX = 0, lastY = 0;
    let az = -0.8, el = 0.5, zoom = 1;
    const onDown = (e) => { dragging = true; lastX = e.clientX; lastY = e.clientY; };
    const onMove = (e) => {
      if (!dragging) return;
      az -= (e.clientX - lastX) * 0.005;
      el = Math.max(-1.5, Math.min(1.5, el + (e.clientY - lastY) * 0.005));
      lastX = e.clientX; lastY = e.clientY;
    };
    const onUp = () => { dragging = false; };
    const onWheel = (e) => { zoom *= e.deltaY > 0 ? 1.1 : 0.91; };
    renderer.domElement.addEventListener('mousedown', onDown);
    window.addEventListener('mousemove', onMove);
    window.addEventListener('mouseup', onUp);
    renderer.domElement.addEventListener('wheel', onWheel, { passive: true });

    let animId;
    const animate = () => {
      animId = requestAnimationFrame(animate);
      const r = span * 1.2 * zoom;
      camera.position.set(
        centre.x + r * Math.cos(el) * Math.cos(az),
        centre.y + r * Math.cos(el) * Math.sin(az),
        centre.z + r * Math.sin(el),
      );
      camera.lookAt(centre);
      renderer.render(scene, camera);
    };
    animate();

    return () => {
      unbindBg();
      cancelAnimationFrame(animId);
      renderer.domElement.removeEventListener('mousedown', onDown);
      window.removeEventListener('mousemove', onMove);
      window.removeEventListener('mouseup', onUp);
      renderer.domElement.removeEventListener('wheel', onWheel);
      renderer.forceContextLoss();
      renderer.dispose();
    };
  }, [positions, displacements, vertexRegion, maxDisp, colorMode, toolpath, showToolpath, showOriginal, defScale]);

  return <div ref={mountRef} className="cte-viewer-wrap" />;
}

// ─── MAIN COMPONENT ───────────────────────────────────────────────────────────
export default function MultiAngleCompensation({ materials }) {
  const mouldMaterials = materials.filter(m => m.category === 'mould');
  const [selId, setSelId] = useState(mouldMaterials[0]?.id || materials[0]?.id || '');
  const [temps, setTemps] = useState({ t1: 23, t2: 120 });
  const mat = materials.find(m => m.id === selId);

  // Toolpath
  const [tpFileName, setTpFileName] = useState(null);
  const [toolpath, setToolpath] = useState(null);
  const [opts, setOpts] = useState({
    useMarkers: true,
    normalSource: 'auto',
    regionAngleTol: 3,
    minRegionLayers: 3,
    layerHeight: 1.0,
    averageInPlane: false,
    blend: 0, // 0 = auto
  });

  // Model
  const { model, loadFile } = useCadModelUpload();
  const modelFileName = model.fileName;
  const positions = model.positions;
  const rawStepBytes = model.rawBytes;
  const meshWarning = model.warning;

  // Registration (model → toolpath frame)
  const [rot, setRot] = useState({ rx: 0, ry: 0, rz: 0 });
  const [offset, setOffset] = useState([0, 0, 0]);
  const [fitInfo, setFitInfo] = useState(null);
  const [moduleInfo, setModuleInfo] = useState(null);
  const autoAlignedFor = useRef(null);

  // UI state
  const [busy, setBusy] = useState(null);
  const [error, setError] = useState(null);
  const [result, setResult] = useState(null);
  const [aiData, setAiData] = useState(null);
  const [showToolpath, setShowToolpath] = useState(true);
  const [showOriginal, setShowOriginal] = useState(true);
  const [colorMode, setColorMode] = useState('displacement');
  const [defScale, setDefScale] = useState(1);
  const [stepReport, setStepReport] = useState(null);

  const tpRef = useRef();
  const modelRef = useRef();

  const invalidate = () => { setResult(null); setStepReport(null); };

  const analysis = useMemo(() => {
    if (!toolpath) return null;
    try {
      return analyseToolpath(toolpath, opts);
    } catch (err) {
      console.error(err);
      return null;
    }
  }, [toolpath, opts]);

  const autoBlend = useMemo(() => {
    if (!analysis || !analysis.regions.length) return 10;
    const diag = Math.hypot(...analysis.bboxMax.map((v, i) => v - analysis.bboxMin[i]));
    return Math.round(Math.min(50, Math.max(4, diag / 40)));
  }, [analysis]);
  const blend = opts.blend > 0 ? opts.blend : autoBlend;

  const strains = useMemo(() => {
    if (!mat) return null;
    return {
      ex: integrateCTE(mat, 'ax', temps.t1, temps.t2) * 1e-6,
      ey: integrateCTE(mat, 'ay', temps.t1, temps.t2) * 1e-6,
      ez: integrateCTE(mat, 'az', temps.t1, temps.t2) * 1e-6,
    };
  }, [mat, temps]);

  const regionTensors = useMemo(() => {
    if (!analysis || !strains) return null;
    return analysis.regions.map(r => regionStrainTensor(r, strains, opts.averageInPlane));
  }, [analysis, strains, opts.averageInPlane]);

  const registration = useMemo(() => ({ R: eulerToMatrix(rot.rx, rot.ry, rot.rz), offset }), [rot, offset]);

  const toolpathSegments = useMemo(() => {
    if (!toolpath || !analysis) return null;
    return buildToolpathSegments(toolpath, analysis.pointRegion, registration);
  }, [toolpath, analysis, registration]);

  const warnings = useMemo(() => {
    if (!toolpath) return [];
    const w = [];
    const st = toolpath.stats;
    if (st.unresolved > 0) w.push(`${st.unresolved.toLocaleString()} motion lines reference named points that were not found — upload the matching .dat with the .src.`);
    if (st.externalAxesVarying) w.push('External axes (E1–E6) change during the program. Positions are used as written in the robot BASE frame; motion of a positioner/turntable is not unwound.');
    if (!st.hasOrientation) w.push('No tool orientation (A/B/C) found — layer normals come from plane fits only.');
    if (st.circAngles) w.push(`${st.circAngles} CIRC move(s) use a CA angle — arcs were swept by that angle; check them in the preview.`);
    if (!st.hasLayerMarkers) w.push('No slicer layer markers found — layers were split on tool-axis changes and height jumps (see Layer height).');
    if (analysis) {
      const np = analysis.layers.filter(L => L.nonPlanar).length;
      if (np) w.push(`${np} layer(s) are non-planar; their normal comes from the tool axis.`);
      if (!analysis.regions.length) w.push('No printable layers detected in this toolpath.');
    }
    return w;
  }, [toolpath, analysis]);

  // ── File handling ──
  const handleToolpathFiles = async (e) => {
    const files = Array.from(e.target.files || []);
    e.target.value = '';
    if (!files.length) return;
    setError(null);
    invalidate();
    setBusy('Parsing toolpath');
    try {
      const texts = await Promise.all(files.map(f => f.text()));
      await new Promise(r => setTimeout(r, 30));
      const isKrl = files.some(f => /\.(src|dat|krl)$/i.test(f.name));
      let tp;
      if (isKrl) {
        // Multi-file KUKA programs: pair each module's .dat with its .src and
        // inline sub-programs where the main program calls them
        const combined = combineKrlFiles(files.map((f, i) => ({ name: f.name, text: texts[i] })));
        tp = parseToolpath(combined.text, 'program.src');
        setModuleInfo(combined.order.length > 1 ? combined : null);
      } else {
        tp = parseToolpath(texts.join('\n'), files[0].name);
        setModuleInfo(null);
      }
      if (!tp.count) throw new Error('No motion commands found');
      setToolpath(tp);
      setTpFileName(files.length > 3 ? `${files.length} files (${files.map(f => f.name).sort()[0]} …)` : files.map(f => f.name).join(' + '));
      setFitInfo(null);
      autoAlignedFor.current = null;
    } catch (err) {
      setError('Failed to parse toolpath: ' + err.message);
      setToolpath(null);
    } finally {
      setBusy(null);
    }
  };

  const handleModelFile = async (e) => {
    const file = e.target.files[0];
    e.target.value = '';
    if (!file) return;
    setError(null);
    invalidate();
    setFitInfo(null);
    autoAlignedFor.current = null;
    await loadFile(file);
  };

  // Fit the CAD to the toolpath in 3D (rotation + offset). Runs automatically
  // once both files are loaded; can be re-run after manual nudges.
  const autoAlign = useCallback(async (fromCurrent = false) => {
    if (!positions || !analysis || !analysis.regions.length) return;
    setBusy('Aligning CAD to toolpath');
    setError(null);
    await new Promise(r => setTimeout(r, 30));
    try {
      const fit = autoRegister(positions, toolpath, analysis, fromCurrent ? { initial: registration } : {});
      setRot(matrixToEuler(fit.R));
      setOffset(fit.offset);
      setFitInfo(fit);
      invalidate();
    } catch (err) {
      setError('Auto-align failed: ' + err.message);
    } finally {
      setBusy(null);
    }
  }, [positions, toolpath, analysis, registration]);

  useEffect(() => {
    if (!positions || !analysis || !analysis.regions.length || !toolpath) return;
    const key = `${tpFileName}|${modelFileName}`;
    if (autoAlignedFor.current === key) return;
    autoAlignedFor.current = key;
    autoAlign();
  }, [positions, analysis, toolpath, tpFileName, modelFileName, autoAlign]);

  const setOpt = (k, v) => { setOpts(o => ({ ...o, [k]: v })); invalidate(); };

  // ── Compensation ──
  const runCompensation = async () => {
    if (!positions || !analysis || !regionTensors || !analysis.regions.length) return;
    setError(null);
    setBusy('Building layer-orientation field');
    await new Promise(r => setTimeout(r, 30));
    try {
      const field = buildLayerField(toolpath, analysis, regionTensors, { blend, registration });
      const comp = compensateMeshWithField(positions, field);

      const regionStats = analysis.regions.map(() => ({ count: 0, sum: 0, max: 0 }));
      const nv = positions.length / 3;
      for (let i = 0; i < nv; i++) {
        const r = comp.vertexRegion[i];
        const m = Math.hypot(comp.displacements[i * 3], comp.displacements[i * 3 + 1], comp.displacements[i * 3 + 2]);
        const st = regionStats[r];
        st.count++; st.sum += m; if (m > st.max) st.max = m;
      }

      setResult({ ...comp, field, regionStats, blend: +(field.cell * 2).toFixed(1), deltaT: temps.t2 - temps.t1, nTriangles: nv / 3 });
      setColorMode('displacement');
      setAiData({
        type: 'compensation',
        materialName: mat.name,
        cureTemp: mat.cureTemp,
        t1: temps.t1,
        t2: temps.t2,
        deltaT: temps.t2 - temps.t1,
        buildAngle: `multi-angle (${analysis.regions.map(r => `${r.tiltDeg.toFixed(0)}°`).join(', ')})`,
        maxCompMM: comp.maxCompMM.toFixed(4),
        avgCompMM: comp.avgCompMM.toFixed(4),
        nTriangles: nv / 3,
        strainX: (strains.ex * 1e6).toFixed(2),
        strainY: (strains.ey * 1e6).toFixed(2),
        strainZ: (strains.ez * 1e6).toFixed(2),
        shearCoupling: analysis.regions.some(r => r.tiltDeg > 0.5),
      });
    } catch (err) {
      console.error(err);
      setError('Compensation failed: ' + err.message);
    } finally {
      setBusy(null);
    }
  };

  const baseName = modelFileName ? modelFileName.replace(/\.[^.]+$/, '') : 'mould';

  const downloadSTL = () => {
    if (!result || !window.confirm(DOWNLOAD_DISCLAIMER)) return;
    downloadBlob(new Blob([exportBinarySTL(result.compensated)], { type: 'application/octet-stream' }),
      `${baseName}_multiangle_compensated.stl`);
  };

  const downloadReport = () => {
    if (!result || !analysis) return;
    const rows = [[
      'Region', 'First layer', 'Last layer', 'Layers', 'Points', 'Tilt (deg)', 'Azimuth (deg)',
      'Normal X', 'Normal Y', 'Normal Z', 'Bead X', 'Bead Y', 'Bead Z', 'Bead alignment',
      'Exx (um/m)', 'Eyy (um/m)', 'Ezz (um/m)', 'Exy (um/m)', 'Exz (um/m)', 'Eyz (um/m)',
      'Avg comp (mm)', 'Max comp (mm)',
    ]];
    analysis.regions.forEach((r, i) => {
      const E = regionTensors[i];
      const st = result.regionStats[i];
      rows.push([
        i + 1, r.layerStart + 1, r.layerEnd + 1, r.layerCount, r.points, r.tiltDeg.toFixed(2), r.azimuthDeg.toFixed(1),
        ...r.normal.map(v => v.toFixed(5)), ...r.beadDir.map(v => v.toFixed(5)), r.beadAlignment.toFixed(2),
        (E[0][0] * 1e6).toFixed(1), (E[1][1] * 1e6).toFixed(1), (E[2][2] * 1e6).toFixed(1),
        (E[0][1] * 1e6).toFixed(1), (E[0][2] * 1e6).toFixed(1), (E[1][2] * 1e6).toFixed(1),
        st.count ? (st.sum / st.count).toFixed(4) : '', st.count ? st.max.toFixed(4) : '',
      ]);
    });
    rows.push([]);
    rows.push(['Material', mat?.name, 'T1', temps.t1, 'T2', temps.t2, 'Blend (mm)', result.blend,
      'Registration rx/ry/rz (deg)', rot.rx, rot.ry, rot.rz, 'Offset', ...offset.map(v => v.toFixed(3))]);
    downloadBlob(new Blob([rows.map(r => r.join(',')).join('\n')], { type: 'text/csv' }), `${baseName}_layer_regions.csv`);
  };

  const downloadSTEP = async () => {
    if (!result || !rawStepBytes || !window.confirm(DOWNLOAD_DISCLAIMER)) return;
    setBusy('Deforming STEP B-rep on server');
    setError(null);
    try {
      const fd = new FormData();
      fd.append('stepFile', new Blob([rawStepBytes], { type: 'application/step' }), modelFileName);
      fd.append('fieldFile', new Blob([serializeLayerField(result.field)], { type: 'application/json' }), 'field.json');
      const submit = await authFetch('/api/cte/compensate-step-field', { method: 'POST', body: fd });
      if (!submit.ok) {
        const e = await submit.json().catch(() => ({}));
        throw new Error(e.error || `Server error ${submit.status}`);
      }
      const { jobId } = await submit.json();
      let report = null;
      for (;;) {
        await new Promise(r => setTimeout(r, 2000));
        const st = await authFetch(`/api/cte/compensate-step-field/status/${jobId}`);
        if (!st.ok) throw new Error('Lost connection to export job');
        const s = await st.json();
        if (s.status === 'error') throw new Error(s.error || 'STEP processing failed on server');
        if (s.status === 'done') { report = s.report; break; }
      }
      const dl = await authFetch(`/api/cte/compensate-step-field/download/${jobId}`);
      if (!dl.ok) throw new Error('Download failed — file may have expired');
      downloadBlob(await dl.blob(), `${baseName}_multiangle_compensated.step`);
      setStepReport(report);
    } catch (err) {
      setError('STEP export failed: ' + err.message);
    } finally {
      setBusy(null);
    }
  };

  const numInput = (value, onChange, props = {}) => (
    <input type="number" value={value} onChange={e => onChange(+e.target.value)} {...props} />
  );

  return (
    <div style={{ maxWidth: 1100 }}>
      {(busy || model.loading) && (
        <div className="cte-loading-overlay">
          <div className="cte-loading-modal">
            <div className="cte-loading-spinner" />
            <div className="cte-loading-title">{busy || 'Parsing CAD file'}</div>
            <div className="cte-loading-sub">{busy && busy.includes('STEP') ? modelFileName : tpFileName || modelFileName}</div>
          </div>
        </div>
      )}

      <div className="cte-info-box" style={{ marginBottom: 16 }}>
        <strong>Dynamic multi-angle CTE scaling.</strong> Upload the robot program (<strong>KUKA .src</strong>,
        plus its <strong>.dat</strong> if points are declared there, or G-code) and the <strong>STEP</strong> of the
        target part. The toolpath is split into layers, each layer's orientation is found from its plane (or the
        tool A/B/C axis), and layers printed at the same angle are grouped into regions. Every region gets its own
        anisotropic CTE tensor (bead / transverse / through-layer), blended smoothly at the joints, so a part printed
        at several angles is scaled correctly in each zone.
      </div>

      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 16, marginBottom: 16 }}>
        <div className="cte-card">
          <div className="cte-card-header">Material &amp; temperatures</div>
          <div className="cte-card-body">
            <div className="cte-form-group" style={{ marginBottom: 12 }}>
              <FieldLabel help="The material the tool is printed in. Its expansion along the bead, across the bead and through the layers comes from the material library.">Printed material</FieldLabel>
              <select value={selId} onChange={e => { setSelId(e.target.value); invalidate(); }}>
                <optgroup label="Mould / Tool Materials">
                  {materials.filter(m => m.category === 'mould').map(m => (
                    <option key={m.id} value={m.id}>{m.name} (v{m.version})</option>
                  ))}
                </optgroup>
                <optgroup label="Layup / Part Materials">
                  {materials.filter(m => m.category === 'layup').map(m => (
                    <option key={m.id} value={m.id}>{m.name} (v{m.version})</option>
                  ))}
                </optgroup>
              </select>
            </div>
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 10 }}>
              <div className="cte-form-group">
                <FieldLabel help="Temperature the CAD dimensions apply at - usually the workshop / measuring temperature.">Room temperature (°C)</FieldLabel>
                {numInput(temps.t1, v => { setTemps(t => ({ ...t, t1: v })); invalidate(); })}
              </div>
              <div className="cte-form-group">
                <FieldLabel help="Temperature the tool is used at (e.g. the cure cycle). The geometry is pre-shrunk so it grows to the CAD size at this temperature.">Working temperature (°C)</FieldLabel>
                {numInput(temps.t2, v => { setTemps(t => ({ ...t, t2: v })); invalidate(); })}
              </div>
            </div>
            {strains && (
              <div className="cte-info-box" style={{ marginTop: 12, fontSize: 'var(--rf-font-size-xs)' }}>
                Growth from room to working temperature: along the bead {(strains.ex * 1e6).toFixed(0)} · across the bead {(strains.ey * 1e6).toFixed(0)} ·
                through the layers {(strains.ez * 1e6).toFixed(0)} µm/m
              </div>
            )}
            <label style={{ display: 'flex', gap: 6, alignItems: 'center', marginTop: 10, fontSize: 'var(--rf-font-size-xs)', textTransform: 'none' }}>
              <input type="checkbox" checked={opts.averageInPlane} onChange={e => setOpt('averageInPlane', e.target.checked)}
                style={{ width: 12, height: 12 }} />
              Beads change direction layer to layer (e.g. 0°/90° raster)
              <HelpTip text="Tick if the bead direction alternates between layers. Each layer then uses the average of the along-bead and across-bead expansion, instead of assuming every bead runs the dominant direction." />
            </label>
          </div>
        </div>

        <div className="cte-card">
          <div className="cte-card-header">Layer detection</div>
          <div className="cte-card-body">
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 10 }}>
              <div className="cte-form-group">
                <FieldLabel help="How the angle of each printed layer is worked out. Automatic uses the shape of the layer (a flat plane through its beads) and falls back to the robot tool angle for curved / non-planar layers.">Layer angle from</FieldLabel>
                <select value={opts.normalSource} onChange={e => setOpt('normalSource', e.target.value)}>
                  <option value="auto">Automatic (recommended)</option>
                  <option value="fit">Shape of each layer</option>
                  <option value="tool">Robot tool angle (A/B/C)</option>
                </select>
              </div>
              <div className="cte-form-group">
                <FieldLabel help="Consecutive layers whose angle differs by less than this are treated as one region with one expansion direction. Smaller = more, finer regions.">Group layers within (°)</FieldLabel>
                {numInput(opts.regionAngleTol, v => setOpt('regionAngleTol', Math.max(0.1, v)), { min: 0.1, step: 0.5 })}
              </div>
              <div className="cte-form-group">
                <FieldLabel help="Short runs of layers - first layers, lead-ins, purge lines, the final retract - are merged into the neighbouring region instead of becoming their own orientation.">Minimum region size (layers)</FieldLabel>
                {numInput(opts.minRegionLayers, v => setOpt('minRegionLayers', Math.max(1, Math.round(v))), { min: 1, step: 1 })}
              </div>
              <div className="cte-form-group">
                <FieldLabel hint={`(0 = auto, ${autoBlend})`} help="Distance over which the scaling blends from one region's orientation to the next, so the compensated part has no steps at the joints. Larger = smoother joints, less local detail.">Blend between regions (mm)</FieldLabel>
                {numInput(opts.blend, v => setOpt('blend', Math.max(0, v)), { min: 0, step: 1 })}
              </div>
              <div className="cte-form-group">
                <FieldLabel help="Only used when the program has no layer markers: a height change bigger than half this value starts a new layer.">Layer height (mm)</FieldLabel>
                {numInput(opts.layerHeight, v => setOpt('layerHeight', Math.max(0.05, v)), { min: 0.05, step: 0.1 })}
              </div>
            </div>
            <label style={{ display: 'flex', gap: 6, alignItems: 'center', marginTop: 10, fontSize: 'var(--rf-font-size-xs)', textTransform: 'none' }}>
              <input type="checkbox" checked={opts.useMarkers} onChange={e => setOpt('useMarkers', e.target.checked)}
                style={{ width: 12, height: 12 }} />
              Use the program's layer markers
              <HelpTip text="Splits layers using ;LAYER / LAYER_CHANGE comments or layer-number variables written by the slicer. Untick to detect layers from the tool angle and height instead." />
            </label>
          </div>
        </div>
      </div>

      <div className="cte-card" style={{ marginBottom: 16 }}>
        <div className="cte-card-header">Files</div>
        <div className="cte-card-body">
          <div style={{ display: 'flex', gap: 12, alignItems: 'center', flexWrap: 'wrap' }}>
            <input ref={tpRef} type="file" multiple accept=".src,.dat,.krl,.gcode,.nc,.gco,.ngc,.tap" onChange={handleToolpathFiles} style={{ display: 'none' }} />
            <button className="cte-btn cte-btn-primary" onClick={() => tpRef.current.click()} disabled={!!busy}>
              1. Robot program
            </button>
            <HelpTip text="Select all the .src and .dat files of the KUKA program together (main program and sub-programs), or a G-code file." />
            <input ref={modelRef} type="file" accept=".step,.stp,.stl,.obj" onChange={handleModelFile} style={{ display: 'none' }} />
            <button className="cte-btn cte-btn-primary" onClick={() => modelRef.current.click()} disabled={!!busy}>
              2. Part CAD
            </button>
            <HelpTip text="The finished part as STEP (exact compensated STEP can be downloaded) or STL. It does not need to be in the same coordinate system as the robot program - it is aligned automatically." />
            {positions && analysis && analysis.regions.length > 0 && (
              <button className="cte-btn cte-btn-primary" style={{ marginLeft: 'auto' }} onClick={runCompensation} disabled={!!busy}>
                Calculate compensation
              </button>
            )}
          </div>
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12, marginTop: 12, fontSize: 'var(--rf-font-size-sm)', color: 'var(--rf-text-secondary)' }}>
            <div>
              <strong>Robot program:</strong> {tpFileName || '—'}
              {toolpath && (
                <div style={{ fontSize: 'var(--rf-font-size-xs)', marginTop: 4 }}>
                  {toolpath.stats.format} · {toolpath.count.toLocaleString()} points
                  {analysis && <> · {analysis.layers.length.toLocaleString()} layers · <span style={{ color: 'var(--rf-success)' }}>{analysis.regions.length} orientation region{analysis.regions.length === 1 ? '' : 's'}</span></>}
                  {moduleInfo && (
                    <div style={{ marginTop: 2 }}>
                      Run order: {moduleInfo.order.join(' → ')}
                      {moduleInfo.notCalled.length > 0 && <span style={{ color: 'var(--rf-warning)' }}> (not called by the main program: {moduleInfo.notCalled.join(', ')})</span>}
                    </div>
                  )}
                </div>
              )}
            </div>
            <div>
              <strong>Part CAD:</strong> {modelFileName || '—'}
              {positions && (
                <div style={{ fontSize: 'var(--rf-font-size-xs)', marginTop: 4 }}>
                  {(positions.length / 9).toLocaleString()} triangles{rawStepBytes && <span style={{ color: 'var(--rf-info)' }}> · STEP (exact export available)</span>}
                </div>
              )}
            </div>
          </div>
          {(error || model.error) && <div style={{ color: 'var(--rf-danger)', fontSize: 'var(--rf-font-size-sm)', marginTop: 10 }}>{error || model.error}</div>}
          {meshWarning && <div className="cte-warn-box" style={{ marginTop: 10 }}><span>Note:</span><span>{meshWarning}</span></div>}
          {warnings.map(w => (
            <div key={w} className="cte-warn-box" style={{ marginTop: 10 }}><span>Check:</span><span>{w}</span></div>
          ))}
        </div>
      </div>

      {analysis && analysis.regions.length > 0 && (
        <div className="cte-card" style={{ marginBottom: 16 }}>
          <div className="cte-card-header">
            <span>Print regions</span>
            <span style={{ fontSize: 'var(--rf-font-size-xs)', color: 'var(--rf-text-muted)' }}>
              Layers printed at the same angle share one expansion direction
            </span>
          </div>
          <div className="cte-card-body" style={{ overflowX: 'auto' }}>
            <table className="cte-dp-table" style={{ width: '100%' }}>
              <thead>
                <tr>
                  <th>Region</th>
                  <th>Layers</th>
                  <th>Tilt <HelpTip text="How far the layers lean from horizontal. 0° = flat layers, 90° = vertical layers." /></th>
                  <th>Lean direction <HelpTip text="Compass direction the layers lean towards, in the robot frame (0° = +X, 90° = +Y)." /></th>
                  <th>Build direction <HelpTip text="Unit vector the layers stack along (x, y, z in the robot frame)." /></th>
                  <th>Bead direction <HelpTip text="Main direction the beads run in this region (x, y, z). Expansion along the bead is usually lowest." /></th>
                  <th>Growth X / Y / Z (µm/m) <HelpTip text="Thermal growth of this region along the robot X, Y and Z axes between room and working temperature." /></th>
                  {result && <th>Avg / max correction (mm)</th>}
                </tr>
              </thead>
              <tbody>
                {analysis.regions.map((r, i) => {
                  const E = regionTensors?.[i];
                  const st = result?.regionStats?.[i];
                  return (
                    <tr key={r.id}>
                      <td><span style={{ display: 'inline-block', width: 10, height: 10, borderRadius: 2, background: regionColor(i), marginRight: 6 }} />{i + 1}</td>
                      <td>{r.layerStart + 1}–{r.layerEnd + 1} ({r.layerCount})</td>
                      <td>{r.tiltDeg.toFixed(1)}°</td>
                      <td>{r.tiltDeg < 0.5 ? '—' : `${r.azimuthDeg.toFixed(0)}°`}</td>
                      <td style={{ fontFamily: 'monospace' }}>{fmtVec(r.normal)}</td>
                      <td style={{ fontFamily: 'monospace' }}>{fmtVec(r.beadDir, 2)}{r.beadAlignment < 0.3 && <span title="Crossed raster — consider averaging in-plane CTE" style={{ color: 'var(--rf-warning)' }}> ×</span>}</td>
                      <td>{E ? `${(E[0][0] * 1e6).toFixed(0)} / ${(E[1][1] * 1e6).toFixed(0)} / ${(E[2][2] * 1e6).toFixed(0)}` : '—'}</td>
                      {result && <td>{st?.count ? `${(st.sum / st.count).toFixed(3)} / ${st.max.toFixed(3)}` : '—'}</td>}
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {positions && analysis && (
        <div className="cte-card" style={{ marginBottom: 16 }}>
          <div className="cte-card-header">
            <span>CAD position on the robot program</span>
            <span style={{ fontSize: 'var(--rf-font-size-xs)', color: 'var(--rf-text-muted)' }}>
              Set automatically - adjust only if the preview looks wrong
            </span>
          </div>
          <div className="cte-card-body">
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(6, 1fr)', gap: 10, alignItems: 'end' }}>
              {[['rx', 'Rotate X'], ['ry', 'Rotate Y'], ['rz', 'Rotate Z']].map(([k, label]) => (
                <div className="cte-form-group" key={k}>
                  <FieldLabel help={`Turns the CAD about the ${label.slice(-1)} axis before it is moved onto the robot program. Use 90° steps to fix a part that is on its side.`}>{label} (°)</FieldLabel>
                  {numInput(+(+rot[k]).toFixed(3), v => { setRot(r => ({ ...r, [k]: v })); setFitInfo(null); invalidate(); }, { step: 90 })}
                </div>
              ))}
              {['X', 'Y', 'Z'].map((ax, i) => (
                <div className="cte-form-group" key={ax}>
                  <FieldLabel help={`Moves the CAD along the robot ${ax} axis after rotating it.`}>Move {ax} (mm)</FieldLabel>
                  {numInput(+offset[i].toFixed(3), v => { setOffset(o => o.map((x, j) => (j === i ? v : x))); setFitInfo(null); invalidate(); }, { step: 1 })}
                </div>
              ))}
            </div>
            <div style={{ display: 'flex', gap: 8, marginTop: 10, flexWrap: 'wrap' }}>
              <button className="cte-btn cte-btn-primary cte-btn-sm" onClick={() => autoAlign(false)} disabled={!!busy}>Auto-align</button>
              <HelpTip text="Finds the rotation and position that put the CAD around the printed beads. Runs by itself when both files are loaded." />
              <button className="cte-btn cte-btn-ghost cte-btn-sm" onClick={() => autoAlign(true)} disabled={!!busy}>Fine-tune from here</button>
              <button className="cte-btn cte-btn-ghost cte-btn-sm" onClick={() => { setRot({ rx: 0, ry: 0, rz: 0 }); setOffset([0, 0, 0]); setFitInfo(null); invalidate(); }}>Reset (CAD already in robot frame)</button>
            </div>
            {fitInfo && (
              <div className={fitInfo.fractionWithin >= 0.8 ? 'cte-info-box' : 'cte-warn-box'} style={{ marginTop: 10, fontSize: 'var(--rf-font-size-xs)' }}>
                <strong>Auto-aligned:</strong> {(fitInfo.fractionWithin * 100).toFixed(0)}% of the toolpath lies within {fitInfo.withinMM.toFixed(0)} mm
                of the CAD volume (median {fitInfo.medianMM.toFixed(1)} mm, 90th percentile {fitInfo.p90MM.toFixed(1)} mm).
                {fitInfo.fractionWithin < 0.8 && ' Fit is weak - check the preview, nudge the rotation and use "Refine from current".'}
                {(fitInfo.sizeRatio > 3 || fitInfo.sizeRatio < 0.33) && (
                  <span style={{ color: 'var(--rf-warning)' }}> The CAD is {fitInfo.sizeRatio.toFixed(1)}x the size of the toolpath - check units (inch vs mm) or that this is the right part.</span>
                )}
              </div>
            )}
            <div className="cte-info-box" style={{ marginTop: 10, fontSize: 'var(--rf-font-size-xs)' }}>
              The CAD and the robot program do not need to share a coordinate system: the CAD is rotated and moved
              onto the toolpath automatically. The coloured toolpath should sit inside the grey model in the preview.
              {result && result.uncoveredFraction > 0.05 && (
                <span style={{ color: 'var(--rf-warning)' }}>
                  {' '}{(result.uncoveredFraction * 100).toFixed(0)}% of the surface is more than {result.blend * 2} mm from any
                  toolpath — check the alignment.
                </span>
              )}
            </div>
          </div>
        </div>
      )}

      {(positions || toolpathSegments) && (
        <div className="cte-card" style={{ marginBottom: 16 }}>
          <div className="cte-card-header">
            <span>{result ? 'Compensated part' : 'Preview - print regions inside the part'}</span>
            <div style={{ display: 'flex', gap: 12, alignItems: 'center', flexWrap: 'wrap' }}>
              {[
                ['Show toolpath', showToolpath, setShowToolpath],
                ...(result ? [['Show original outline', showOriginal, setShowOriginal]] : []),
              ].map(([label, val, set]) => (
                <label key={label} style={{ display: 'flex', gap: 6, alignItems: 'center', cursor: 'pointer', textTransform: 'none', fontSize: 'var(--rf-font-size-xs)', color: 'var(--rf-text-secondary)' }}>
                  <input type="checkbox" checked={val} onChange={e => set(e.target.checked)} style={{ width: 12, height: 12 }} />
                  {label}
                </label>
              ))}
              {result && (
                <select value={colorMode} onChange={e => setColorMode(e.target.value)} style={{ fontSize: 'var(--rf-font-size-xs)', padding: '2px 6px' }}>
                  <option value="displacement">Colour by correction size</option>
                  <option value="region">Colour by print region</option>
                </select>
              )}
              {result && (
                <>
                  <button className="cte-btn cte-btn-primary cte-btn-sm" onClick={downloadSTL}>Download STL</button>
                  {rawStepBytes && (
                    <button className="cte-btn cte-btn-primary cte-btn-sm" onClick={downloadSTEP} disabled={!!busy}>Download STEP</button>
                  )}
                  <button className="cte-btn cte-btn-ghost cte-btn-sm" onClick={downloadReport}>Region report (CSV)</button>
                  <HelpTip text="STL: compensated mesh. STEP: the exact CAD surfaces moved by the compensation (processed on the server). CSV: angle, bead direction and growth for each print region." />
                </>
              )}
            </div>
          </div>
          <div className="cte-card-body">
            {result && (
              <div className="cte-stats-grid" style={{ marginBottom: 12 }}>
                {[
                  { label: 'Max Compensation', value: result.maxCompMM.toFixed(4), unit: 'mm' },
                  { label: 'Avg Compensation', value: result.avgCompMM.toFixed(4), unit: 'mm' },
                  { label: 'Regions', value: analysis.regions.length, unit: `blend ${result.blend} mm` },
                  { label: 'Delta T', value: result.deltaT.toFixed(1), unit: 'deg C' },
                ].map(({ label, value, unit }) => (
                  <div key={label} className="cte-stat-box">
                    <div className="cte-stat-label">{label}</div>
                    <div className="cte-stat-value">{value}</div>
                    <div className="cte-stat-unit">{unit}</div>
                  </div>
                ))}
              </div>
            )}
            {stepReport && (
              <div className={stepReport.valid ? 'cte-info-box' : 'cte-warn-box'} style={{ marginBottom: 12, fontSize: 'var(--rf-font-size-xs)' }}>
                STEP exported: {stepReport.faces} faces, {stepReport.controlPoints.toLocaleString()} control points moved
                (max {stepReport.maxCompMM.toFixed(4)} mm) in {stepReport.seconds.toFixed(1)} s —
                {stepReport.valid ? ' B-rep check passed.' : ' B-rep check reported issues, inspect the solid before use.'}
              </div>
            )}
            <LayerFieldViewer
              positions={positions}
              displacements={result?.displacements}
              vertexRegion={result?.vertexRegion}
              maxDisp={result?.maxCompMM || 0}
              colorMode={result ? colorMode : 'none'}
              toolpath={toolpathSegments}
              showToolpath={showToolpath}
              showOriginal={showOriginal}
              defScale={result ? defScale : 1}
            />
            {result && colorMode === 'displacement' && <ColorBar maxVal={result.maxCompMM} unit="mm" />}
            {result && (
              <div className="cte-timeline-controls" style={{ marginTop: 8 }}>
                <span style={{ fontSize: 'var(--rf-font-size-xs)', color: 'var(--rf-text-muted)', whiteSpace: 'nowrap' }}>Visual scale (display only)</span>
                <input type="range" className="cte-timeline-slider" min={1} max={50} value={defScale} onChange={e => setDefScale(+e.target.value)} />
                <span style={{ fontSize: 'var(--rf-font-size-xs)', color: 'var(--rf-primary)', fontWeight: 600, whiteSpace: 'nowrap' }}>
                  {defScale === 1 ? 'true scale' : `${defScale}× shown larger`}
                </span>
              </div>
            )}
            <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap', marginTop: 10 }}>
              {analysis?.regions.map((r, i) => (
                <span key={r.id} style={{ fontSize: 'var(--rf-font-size-xs)', color: 'var(--rf-text-secondary)' }}>
                  <span style={{ display: 'inline-block', width: 10, height: 10, borderRadius: 2, background: regionColor(i), marginRight: 4 }} />
                  Region {i + 1}: {r.tiltDeg.toFixed(0)}°
                </span>
              ))}
            </div>
          </div>
        </div>
      )}

      {aiData && result && <CTEAIPanel analysisData={aiData} label="Multi-Angle Compensation — AI Insight" />}
    </div>
  );
}
